import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { amiGet, amiPost, amiPatch, Card, Loading, NotYet, StatusDot, isoFmt, errMsg } from "@/lib/ami";

const EMPTY = { name: "", env: "demo", region: "eea", api_key: "", secret_key: "", passphrase: "", password: "" };

function Field({ label, children }) {
  return <label className="block"><span className="text-xs text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>;
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function NewConnection({ onDone }) {
  const [f, setF] = useState(EMPTY);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: typeof v === "string" ? v : v.target.value }));
  const m = useMutation({
    mutationFn: () => amiPost("/connections", { ...f, password: f.env === "live" ? f.password : undefined }),
    onSuccess: (c) => { (c.last_test_ok ? toast.success : toast.error)(c.last_test_msg); setF(EMPTY); onDone(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <Card title="Neue OKX-Verbindung" testid="ami-new-connection">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Verbindungsname"><Input value={f.name} onChange={set("name")} data-testid="ami-conn-name" /></Field>
        <Field label="Handelsumgebung"><Select value={f.env} onChange={set("env")} options={[["demo", "OKX Demo (kein Echtgeld)"], ["live", "OKX Live (nur lesend)"]]} testid="ami-conn-env" /></Field>
        <Field label="Region"><Select value={f.region} onChange={set("region")} options={[["eea", "EEA (my.okx.com, DE/AT)"], ["global", "Global"], ["us", "US"]]} testid="ami-conn-region" /></Field>
        <Field label="API Key"><Input value={f.api_key} onChange={set("api_key")} autoComplete="off" data-testid="ami-conn-key" /></Field>
        <Field label="Secret Key"><PasswordInput value={f.secret_key} onChange={set("secret_key")} autoComplete="off" data-testid="ami-conn-secret" /></Field>
        <Field label="API Passphrase"><PasswordInput value={f.passphrase} onChange={set("passphrase")} autoComplete="off" data-testid="ami-conn-passphrase" /></Field>
        {f.env === "live" && <Field label="Dein TradeMind-Passwort (Bestätigung)"><PasswordInput value={f.password} onChange={set("password")} data-testid="ami-conn-reauth" /></Field>}
      </div>
      <p className="mt-4 text-xs text-muted-foreground leading-relaxed">Secrets werden verschlüsselt auf dem Server gespeichert und nie wieder angezeigt. Keys mit Auszahlungsrecht werden abgelehnt. Für Phase 1 genügt „Lesen".</p>
      <Button className="mt-4" onClick={() => m.mutate()} disabled={m.isPending || !f.name || !f.api_key || !f.secret_key || !f.passphrase} data-testid="ami-conn-save">
        {m.isPending ? "Teste Verbindung…" : "Speichern & testen"}
      </Button>
    </Card>
  );
}

function ConnRow({ c, refresh }) {
  const [pw, setPw] = useState("");
  const act = (fn) => fn().then((r) => { if (r?.last_test_msg) (r.last_test_ok ? toast.success : toast.error)(r.last_test_msg); refresh(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="py-4 border-b border-border/60 last:border-0" data-testid={`ami-conn-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <StatusDot ok={c.status === "aktiv"} warn={c.last_test_ok === false} />
          <div>
            <div className="font-medium">{c.name} <span className="text-xs text-muted-foreground uppercase ml-1">{c.env} · {c.region}</span></div>
            <div className="text-xs text-muted-foreground font-mono">Key {c.key_hint} · {c.status} · Rechte: {c.perm || "–"} · getestet {isoFmt(c.last_test_at)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" variant="secondary" onClick={() => act(() => amiPost(`/connections/${c.id}/test`))} data-testid={`ami-conn-test-${c.id}`}>Testen</Button>
          {c.status === "aktiv"
            ? <Button size="sm" variant="ghost" onClick={() => act(() => amiPatch(`/connections/${c.id}`, { status: "deaktiviert" }))} data-testid={`ami-conn-deactivate-${c.id}`}>Deaktivieren</Button>
            : <Button size="sm" variant="ghost" onClick={() => act(() => amiPatch(`/connections/${c.id}`, { status: "aktiv" }))} data-testid={`ami-conn-activate-${c.id}`}>Aktivieren</Button>}
          {c.env === "live" && <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Passwort" className="h-8 w-36" data-testid={`ami-conn-delete-pw-${c.id}`} />}
          <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => window.confirm(`Verbindung "${c.name}" löschen?`) && act(() => amiPost(`/connections/${c.id}/delete`, { password: pw }))} data-testid={`ami-conn-delete-${c.id}`}>Löschen</Button>
        </div>
      </div>
      {c.last_test_msg && <div className={`mt-2 text-xs ${c.last_test_ok ? "text-muted-foreground" : "text-amber-300/90"}`}>{c.last_test_msg}</div>}
    </div>
  );
}

export default function AmiConnections() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["ami-connections"], queryFn: () => amiGet("/connections") });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ami-connections"] }); qc.invalidateQueries({ queryKey: ["ami-status"] }); qc.invalidateQueries({ queryKey: ["ami-account"] }); };
  return (
    <div className="space-y-6" data-testid="ami-connections">
      <Card title="OKX-Verbindungen" testid="ami-connections-list">
        {q.isLoading ? <Loading /> : q.data.length === 0 ? <NotYet text="Noch keine Verbindung. Lege zuerst deinen OKX-Demo-Key an." /> : q.data.map((c) => <ConnRow key={c.id} c={c} refresh={refresh} />)}
        <p className="mt-3 text-xs text-muted-foreground">Pro Umgebung ist genau eine Verbindung aktiv. Demo und Live sind technisch getrennt; Live ist in Phase 1 nur lesend, Handel ist serverseitig gesperrt.</p>
      </Card>
      <NewConnection onDone={refresh} />
    </div>
  );
}
